"use client";

import Link from "next/link";
import { useEffect, useState } from "react";
import { api, type MarketListItem, type TopicRecord } from "@/lib/api";

/**
 * EPIC-006 STORY-608 — `@market` mention auto-linking.
 * EPIC-022 STORY-626 — `@user` mention auto-linking on comments.
 *
 * Scans a free-text body for `@<token>` tokens. Resolution order:
 *   1. Match against the cached market slug list (STORY-608)
 *   2. Match against the cached follower-graph usernames (STORY-626)
 *   3. Otherwise leave as plain `@<text>`
 *
 * Posts use server-side resolution (PostsService.expandMentionsAndTags
 * preprocessor); comments stay client-side because the resolution set
 * for a comment thread is small and the cost of client-side lookup is
 * a single fetch on page mount.
 *
 * Closed-beta scope:
 *   - Slug-only matching today (case-insensitive). Title-substring
 *     fuzzy match + disambiguation popover deferred until we hit
 *     enough markets that slug guessing becomes hard
 *   - Markets list fetched once on mount via the existing public
 *     `/v1/markets` endpoint (already cached + cheap). For a
 *     comments-only render, this is one fetch per page mount
 *     amortized across every comment on the page
 *
 * Hover preview tooltip: deferred. The link itself takes the user
 * there in one click; the cost of inline preview infra (extra
 * fetches per hover, popover lib) outweighs the value at closed-
 * beta scale.
 */

let cachedMarkets: Promise<MarketListItem[]> | null = null;
let cachedTopics: Promise<TopicRecord[]> | null = null;

function loadMarketsOnce(): Promise<MarketListItem[]> {
  if (!cachedMarkets) {
    // Backend caps `limit` at 100 (ListMarketsDto.limit @Max(100)).
    // 2026-04-30: caught from a console 400 (Bad Request) on
    // /v1/markets?limit=200. The original 200 was over the cap; for
    // closed beta with <30 active markets the 100 ceiling is
    // plenty. Revisit only if the active-market count approaches
    // 100, at which point pagination is the right tool, not a
    // larger limit.
    cachedMarkets = api.listMarkets({ limit: 100 }).then((r) => r.markets);
    // If the fetch fails, allow the next call to retry rather than
    // permanently caching the rejection.
    cachedMarkets.catch(() => {
      cachedMarkets = null;
    });
  }
  return cachedMarkets;
}

// 2026-05-05 — topic mention support (founder ask "topic, the market
// or the user to tag"). Same one-shot cache pattern as markets; the
// closed-beta topic catalog is small enough to fetch in one round.
function loadTopicsOnce(): Promise<TopicRecord[]> {
  if (!cachedTopics) {
    cachedTopics = api.listTopics().then((r) => r.topics);
    cachedTopics.catch(() => {
      cachedTopics = null;
    });
  }
  return cachedTopics;
}

const MENTION_REGEX = /@([a-z0-9][a-z0-9_-]{1,80})/gi;
// STORY-626 — username token shape (letters/digits/underscore only,
// no hyphens). When a `@<token>` doesn't match a market slug but
// fits the username shape, fall through to a /u/<token> link. The
// destination route's "User not found" placeholder handles
// non-existent usernames cleanly; over-eager linking is fine because
// the cost of a click → 404 is much lower than the cost of NOT
// linking real mentions.
const USERNAME_TOKEN_REGEX = /^[a-z0-9_]{3,30}$/i;

interface MentionParserProps {
  text: string;
  className?: string;
}

export function MentionParser({ text, className }: MentionParserProps) {
  const [slugs, setSlugs] = useState<Set<string> | null>(null);
  const [topicSlugs, setTopicSlugs] = useState<Set<string> | null>(null);

  useEffect(() => {
    let cancelled = false;
    void loadMarketsOnce()
      .then((markets) => {
        if (cancelled) return;
        setSlugs(new Set(markets.map((m) => m.slug.toLowerCase())));
      })
      .catch(() => {
        if (!cancelled) setSlugs(new Set());
      });
    void loadTopicsOnce()
      .then((topics) => {
        if (cancelled) return;
        setTopicSlugs(new Set(topics.map((tp) => tp.slug.toLowerCase())));
      })
      .catch(() => {
        if (!cancelled) setTopicSlugs(new Set());
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const segments = parseSegments(text, slugs, topicSlugs);

  return (
    <span className={className}>
      {segments.map((s, i) => {
        if (s.kind === "text") {
          return <span key={i}>{s.value}</span>;
        }
        if (s.kind === "market") {
          return (
            <Link
              key={i}
              href={`/markets/${s.slug}`}
              className="font-bold text-[var(--color-brand)] hover:underline"
              onClick={(e) => e.stopPropagation()}
            >
              @{s.slug}
            </Link>
          );
        }
        if (s.kind === "topic") {
          return (
            <Link
              key={i}
              href={`/topic/${s.slug}`}
              className="font-bold text-[var(--color-brand)] hover:underline"
              onClick={(e) => e.stopPropagation()}
            >
              @{s.slug}
            </Link>
          );
        }
        // STORY-626 — username link. The /u/<username> route handles
        // non-existent usernames with a clean 404 placeholder.
        return (
          <Link
            key={i}
            href={`/u/${s.username}`}
            className="font-bold text-[var(--color-brand)] hover:underline"
            onClick={(e) => e.stopPropagation()}
          >
            @{s.username}
          </Link>
        );
      })}
    </span>
  );
}

type Segment =
  | { kind: "text"; value: string }
  | { kind: "market"; slug: string }
  | { kind: "topic"; slug: string }
  | { kind: "user"; username: string };

function parseSegments(
  text: string,
  slugs: Set<string> | null,
  topicSlugs: Set<string> | null,
): Segment[] {
  if (!slugs || !topicSlugs) {
    // While either index is still loading, render the text raw to
    // avoid a flash of un-linked mentions that re-renders into links.
    return [{ kind: "text", value: text }];
  }
  const out: Segment[] = [];
  let lastIndex = 0;
  // Reset regex state in case the same instance is reused across
  // renders (the regex above is module-level so we reset explicitly).
  MENTION_REGEX.lastIndex = 0;
  let match: RegExpExecArray | null;
  while ((match = MENTION_REGEX.exec(text)) !== null) {
    const [whole, slugRaw] = match;
    const slug = slugRaw.toLowerCase();
    if (match.index > lastIndex) {
      out.push({ kind: "text", value: text.slice(lastIndex, match.index) });
    }
    if (slugs.has(slug)) {
      out.push({ kind: "market", slug });
    } else if (topicSlugs.has(slug)) {
      out.push({ kind: "topic", slug });
    } else if (USERNAME_TOKEN_REGEX.test(slug)) {
      // STORY-626 — token shape matches a username (no hyphens).
      // Link to /u/<token>; route handles non-existent users.
      out.push({ kind: "user", username: slug });
    } else {
      // Unmatched: keep as plain text including the @.
      out.push({ kind: "text", value: whole });
    }
    lastIndex = match.index + whole.length;
  }
  if (lastIndex < text.length) {
    out.push({ kind: "text", value: text.slice(lastIndex) });
  }
  return out;
}
